import { FC, useRef } from "react";
import { DateRangePicker, Stack } from "rsuite";
import subDays from "date-fns/subDays";
import startOfWeek from "date-fns/startOfWeek";
import endOfWeek from "date-fns/endOfWeek";
import addDays from "date-fns/addDays";
import startOfMonth from "date-fns/startOfMonth";
import endOfMonth from "date-fns/endOfMonth";
import addMonths from "date-fns/addMonths";

import "./DateRange.scss";

const predefinedRanges: any = [
  {
    label: "Today",
    value: [new Date(), new Date()],
    placement: "left",
  },
  {
    label: "Yesterday",
    value: [addDays(new Date(), -1), addDays(new Date(), -1)],
    placement: "left",
  },
  {
    label: "This week",
    value: [startOfWeek(new Date()), endOfWeek(new Date())],
    placement: "left",
  },
  {
    label: "Last 7 days",
    value: [subDays(new Date(), 6), new Date()],
    placement: "left",
  },
  {
    label: "Last 30 days",
    value: [subDays(new Date(), 29), new Date()],
    placement: "left",
  },
  {
    label: "This month",
    value: [startOfMonth(new Date()), new Date()],
    placement: "left",
  },
  {
    label: "Last month",
    value: [startOfMonth(addMonths(new Date(), -1)), endOfMonth(addMonths(new Date(), -1))],
    placement: "left",
  },
  {
    label: "This year",
    value: [new Date(new Date().getFullYear(), 0, 1), new Date()],
    placement: "left",
  },
  {
    label: "Last year",
    value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date(new Date().getFullYear(), 0, 0)],
    placement: "left",
  },
  {
    label: "All time",
    value: [new Date(new Date().getFullYear() - 1, 0, 1), new Date()],
    placement: "left",
  },
  {
    label: "Last week",
    closeOverlay: false,
    value: (value: any) => {
      const [start = new Date()] = value || [];
      return [addDays(startOfWeek(start, { weekStartsOn: 0 }), -7), addDays(endOfWeek(start, { weekStartsOn: 0 }), -7)];
    },
    appearance: "default",
  },
  {
    label: "Next week",
    closeOverlay: false,
    value: (value: any) => {
      const [start = new Date()] = value || [];
      return [addDays(startOfWeek(start, { weekStartsOn: 0 }), 7), addDays(endOfWeek(start, { weekStartsOn: 0 }), 7)];
    },
    appearance: "default",
  },
];

const defaultResetValueAlternative = [subDays(new Date(), 29), new Date()] as [Date, Date];

const DateRange: FC<DateRangeProps> = ({
  handleDateSelect,
  placeholder = "Date",
  styles,
  defaultResetValue,
  useAsInstantDropdown = false,
  handleCustomLabel,
  value,
  label,
}) => {
  const dateRangeRef = useRef<HTMLDivElement>(null);
  return (
    <Stack
      direction="column"
      spacing={8}
      alignItems="flex-start"
      ref={useAsInstantDropdown ? dateRangeRef : null}
      className={`date-range-container  ${useAsInstantDropdown ? "instant-drop-down" : ""}`}
    >
      {label && (
        <label className="w-[100%] label text-[#6A6A6A] text-[14px] !-mb-5" htmlFor="">
          {label}
        </label>
      )}
      <DateRangePicker
        editable={false}
        container={() => dateRangeRef.current as HTMLElement}
        ranges={predefinedRanges}
        placeholder={placeholder}
        style={{ ...styles }}
        value={value}
        onShortcutClick={(shortcut, event) => {
          shortcut?.value && handleDateSelect(shortcut?.value as [Date, Date]);
          shortcut?.label && handleCustomLabel && handleCustomLabel(String(shortcut?.label));
        }}
        open={useAsInstantDropdown ? true : undefined}
        block={useAsInstantDropdown ? true : undefined}
        placement={useAsInstantDropdown ? "bottom" : "bottomEnd"}
        onClean={() => handleDateSelect(defaultResetValue ? defaultResetValue : defaultResetValueAlternative)}
        onOk={(date) => {
          handleDateSelect(date);
          handleCustomLabel && handleCustomLabel("Custom Range");
        }}
      />
    </Stack>
  );
};

export default DateRange;
